import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { canAccessPage } from "@/app/router/nav.config";
import { LoadingState } from "@/components/feedback/LoadingState";
import { useMyPageAccess } from "@/features/team/useTeam";
import { useAuthStore } from "@/store/auth.store";

/** Redirects to /login when there is no valid session. */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

/** Redirects an already-signed-in admin away from /login, to /dashboard. */
export function GuestRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Redirects to /403 when the signed-in admin's role can't see `path`, per
 * their SUPER_ADMIN-assigned custom role's page list. SUPER_ADMIN always
 * passes without waiting on that fetch; everyone else waits for it — there
 * is no static fallback anymore now that roles are fully custom, so
 * guessing here would risk a false allow.
 */
export function RoleGuard({ path, children }: { path: string; children: ReactNode }) {
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");
  const { data, isLoading } = useMyPageAccess();

  if (!isSuperAdmin && isLoading) {
    return <LoadingState label="Checking access…" />;
  }
  if (!canAccessPage(path, isSuperAdmin, data?.pages)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}
